/**
 * Workflow Hooks
 *
 * React Query hooks for workflow management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { workflowService } from '../services/workflow';
import {
  CreateWorkflowRequest,
  UpdateWorkflowRequest,
  WorkflowListParams,
  WorkflowStatus,
  RunWorkflowRequest,
} from '../types/workflow';

// Query keys for workflows
export const workflowKeys = {
  all: ['workflows'] as const,
  lists: () => [...workflowKeys.all, 'list'] as const,
  list: (params: WorkflowListParams | Record<string, unknown>) => [...workflowKeys.lists(), params] as const,
  details: () => [...workflowKeys.all, 'detail'] as const,
  detail: (id: string) => [...workflowKeys.details(), id] as const,
  stats: () => [...workflowKeys.all, 'stats'] as const,
  executions: () => [...workflowKeys.all, 'executions'] as const,
  executionList: (params: Record<string, unknown>) => [...workflowKeys.executions(), params] as const,
};

/**
 * Hook to list workflows with pagination and filters
 *
 * @param params - Query parameters for filtering
 * @returns Query result with paginated workflows
 */
export function useWorkflows(params?: WorkflowListParams) {
  return useQuery({
    queryKey: workflowKeys.list(params || {}),
    queryFn: () => workflowService.list(params),
  });
}

/**
 * Hook to fetch a single workflow
 *
 * @param workflowId - The workflow ID to fetch
 * @returns Query result with workflow data
 */
export function useWorkflow(workflowId: string | undefined) {
  return useQuery({
    queryKey: workflowKeys.detail(workflowId!),
    queryFn: () => workflowService.get(workflowId!),
    enabled: !!workflowId,
  });
}

/**
 * Hook to create a workflow
 *
 * @returns Mutation object for creating a workflow
 */
export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWorkflowRequest) => workflowService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      toast.success('Workflow created successfully!', { icon: '🔄' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to create workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to update a workflow
 *
 * @returns Mutation object for updating a workflow
 */
export function useUpdateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWorkflowRequest }) =>
      workflowService.update(id, data),
    onSuccess: (workflow) => {
      queryClient.setQueryData(workflowKeys.detail(workflow.id), workflow);
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      toast.success('Workflow updated successfully!');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to update workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to delete a workflow
 *
 * @returns Mutation object for deleting a workflow
 */
export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) => workflowService.delete(workflowId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      toast.success('Workflow deleted successfully!', { icon: '🗑️' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to delete workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to activate a workflow
 *
 * @returns Mutation object for activating a workflow
 */
export function useActivateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) => workflowService.activate(workflowId),
    onSuccess: (workflow) => {
      queryClient.setQueryData(workflowKeys.detail(workflow.id), workflow);
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      toast.success('Workflow activated!', { icon: '✅' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to activate workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to pause a workflow
 *
 * @returns Mutation object for pausing a workflow
 */
export function usePauseWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) => workflowService.pause(workflowId),
    onSuccess: (workflow) => {
      queryClient.setQueryData(workflowKeys.detail(workflow.id), workflow);
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      toast.success('Workflow paused', { icon: '⏸️' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to pause workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to archive a workflow
 *
 * @returns Mutation object for archiving a workflow
 */
export function useArchiveWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowId: string) => workflowService.archive(workflowId),
    onSuccess: (workflow) => {
      queryClient.setQueryData(workflowKeys.detail(workflow.id), workflow);
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      toast.success('Workflow archived', { icon: '📦' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to archive workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to run a workflow
 *
 * @returns Mutation object for running a workflow
 */
export function useRunWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ workflowId, data }: { workflowId: string; data?: RunWorkflowRequest }) =>
      workflowService.run(workflowId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.executions() });
      toast.success(`Workflow run started! Run ID: ${response.runId.slice(0, 8)}...`, {
        icon: '🚀',
        duration: 4000,
      });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to run workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to clone a workflow
 *
 * @returns Mutation object for cloning a workflow
 */
export function useCloneWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      workflowService.clone(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workflowKeys.lists() });
      queryClient.invalidateQueries({ queryKey: workflowKeys.stats() });
      toast.success('Workflow cloned successfully!', { icon: '📋' });
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message || 'Failed to clone workflow';
      toast.error(message);
    },
  });
}

/**
 * Hook to fetch workflow statistics
 *
 * @returns Query result with workflow statistics
 */
export function useWorkflowStats() {
  return useQuery({
    queryKey: workflowKeys.stats(),
    queryFn: () => workflowService.getStats(),
  });
}

/**
 * Hook to fetch workflow executions
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Query result with workflow executions
 */
export function useWorkflowExecutions(params?: {
  page?: number;
  limit?: number;
  status?: string;
}) {
  return useQuery({
    queryKey: workflowKeys.executionList(params || {}),
    queryFn: () => workflowService.listExecutions(params),
    refetchInterval: 5000, // Refresh every 5 seconds to catch status updates
  });
}

/**
 * Hook to fetch executions for a specific workflow
 *
 * @param workflowId - Workflow ID
 * @param params - Query parameters for filtering and pagination
 * @returns Query result with workflow executions
 */
export function useWorkflowExecutionsForWorkflow(
  workflowId: string | undefined,
  params?: { page?: number; limit?: number; status?: string }
) {
  return useQuery({
    queryKey: [...workflowKeys.executions(), workflowId, params || {}],
    queryFn: () => workflowService.listWorkflowExecutions(workflowId!, params),
    enabled: !!workflowId,
    refetchInterval: 5000, // Refresh every 5 seconds
  });
}

/**
 * Hook to fetch a single workflow execution with details
 *
 * @param workflowId - Workflow ID
 * @param executionId - Execution ID
 * @returns Query result with workflow execution details
 */
export function useWorkflowExecution(workflowId: string | undefined, executionId: string | undefined) {
  return useQuery({
    queryKey: [...workflowKeys.executions(), workflowId, executionId],
    queryFn: () => workflowService.getExecution(workflowId!, executionId!),
    enabled: !!workflowId && !!executionId,
    refetchInterval: (query) => {
      // Refetch every 2 seconds while running
      const status = query.state.data?.status;
      if (status === 'RUNNING' || status === 'PENDING') {
        return 2000;
      }
      return false;
    },
  });
}

/**
 * Helper to get status badge color
 */
export function getWorkflowStatusColor(status: WorkflowStatus): string {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-800';
    case 'ACTIVE':
      return 'bg-green-100 text-green-800';
    case 'PAUSED':
      return 'bg-yellow-100 text-yellow-800';
    case 'ARCHIVED':
      return 'bg-slate-100 text-slate-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

/**
 * Helper to get status icon
 */
export function getWorkflowStatusIcon(status: WorkflowStatus): string {
  switch (status) {
    case 'DRAFT':
      return '📝';
    case 'ACTIVE':
      return '✅';
    case 'PAUSED':
      return '⏸️';
    case 'ARCHIVED':
      return '📦';
    default:
      return '❓';
  }
}

/**
 * Helper to get execution mode label
 */
export function getExecutionModeLabel(mode: string): string {
  switch (mode) {
    case 'SEQUENTIAL':
      return 'Sequential';
    case 'PARALLEL':
      return 'Parallel';
    case 'CONDITIONAL':
      return 'Conditional';
    case 'FAN_OUT':
      return 'Fan-out';
    case 'LOOP':
      return 'Loop';
    default:
      return mode;
  }
}

/**
 * Helper to get execution mode icon
 */
export function getExecutionModeIcon(mode: string): string {
  switch (mode) {
    case 'SEQUENTIAL':
      return '➡️';
    case 'PARALLEL':
      return '⚡';
    case 'CONDITIONAL':
      return '🔀';
    case 'FAN_OUT':
      return '📤';
    case 'LOOP':
      return '🔁';
    default:
      return '❓';
  }
}

/**
 * Helper to get trigger type label
 */
export function getTriggerTypeLabel(type: string): string {
  switch (type) {
    case 'MANUAL':
      return 'Manual';
    case 'SCHEDULED':
      return 'Scheduled';
    case 'WEBHOOK':
      return 'Webhook';
    case 'EVENT':
      return 'Event';
    default:
      return type;
  }
}

/**
 * Helper to get trigger type icon
 */
export function getTriggerTypeIcon(type: string): string {
  switch (type) {
    case 'MANUAL':
      return '👆';
    case 'SCHEDULED':
      return '⏰';
    case 'WEBHOOK':
      return '🔗';
    case 'EVENT':
      return '📡';
    default:
      return '❓';
  }
}
